import { useAuth } from '@clerk/react'
import { useCallback } from 'react'

/**
 * Error de una función del portal. `status` permite distinguir en pantalla:
 * 401 sin sesión, 403 acceso revocado, 404 no permitido, 503 sin configurar.
 */
export class ErrorApi extends Error {
  readonly status: number

  constructor(status: number, mensaje: string) {
    super(mensaje)
    this.status = status
  }
}

async function pedir<T>(
  ruta: string,
  token: string | null,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers)
  headers.set('Accept', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const respuesta = await conReintento(() => fetch(ruta, { ...init, headers }), init)

  if (!respuesta.ok) {
    let mensaje = 'No pudimos completar la acción. Probá de nuevo.'
    try {
      const cuerpo = (await respuesta.json()) as { error?: string }
      if (cuerpo.error) mensaje = cuerpo.error
    } catch {
      // Sin cuerpo JSON: queda el mensaje general, no el del servidor.
    }
    throw new ErrorApi(respuesta.status, mensaje)
  }

  if (respuesta.status === 204) return null as T
  return (await respuesta.json()) as T
}

/**
 * Las lecturas se reintentan una vez ante un corte de red o un 5xx: la base
 * de develop duerme tras cinco minutos y la primera consulta puede fallar
 * mientras despierta. Las escrituras no se repiten solas.
 */
async function conReintento(
  intentar: () => Promise<Response>,
  init?: RequestInit,
): Promise<Response> {
  const esLectura = !init?.method || init.method.toUpperCase() === 'GET'
  try {
    const r = await intentar()
    if (esLectura && r.status >= 500) {
      await esperar(800)
      return await intentar()
    }
    return r
  } catch (error) {
    if (!esLectura) throw error
    await esperar(800)
    return await intentar()
  }
}

const esperar = (ms: number) =>
  new Promise<void>((resolver) => {
    setTimeout(resolver, ms)
  })

/**
 * `fetch` con la sesión de Clerk. Toda llamada a /api/* pasa por acá:
 * el token viaja en `Authorization` y lo verifica `withAuth` del otro lado.
 */
export function useApi() {
  const { getToken } = useAuth()

  return useCallback(
    async <T>(ruta: string, init?: RequestInit): Promise<T> => {
      const token = await getToken()
      return pedir<T>(ruta, token, init)
    },
    [getToken],
  )
}
